<template>
    <div id="app">
        <Header></Header>
        <main class="adjuster">
            <Splitpanes class="brench">
                <Pane size="35" class="task-text">
                    <div class="title">
                        {{ title }}
                        <a-tag v-if="passed" color="green">已通过</a-tag>
                    </div>
                    <div class="task-detail" v-html="content"></div>
                </Pane>
                <Pane>
                    <Splitpanes horizontal>
                        <Pane size="65">
                            <div class="editor" ref="editor"></div>
                        </Pane>
                        <Pane>
                            <div class="toolbar">
                                <div class="status">
                                    <span v-if="status === 'accept'" class="accept">通过</span>
                                    <span v-else-if="status === 'normal'" class="normal">运行完成</span>
                                    <span v-else-if="status === 'syntax error'" class="syntax-error">语法错误</span>
                                    <span v-else-if="status === 'wrong answer'" class="wrong-answer">答案错误</span>
                                    <span v-else-if="status === 'runtime error'" class="runtime-error">运行错误</span>
                                </div>
                                <a-button @click="run">运行</a-button>
                                <a-button type="primary" @click="judge">提交</a-button>
                            </div>
                            <div class="output">
                                <pre>{{ output }}</pre>
                            </div>
                        </Pane>
                    </Splitpanes>
                </Pane>
            </Splitpanes>
        </main>
        <Footer></Footer>
    </div>
</template>

<script>
import Header from '../components/Header.vue';
import Footer from '@/components/Footer';
import { parseTaskMarkdown } from '../utils';
import * as monaco from 'monaco-editor';
import { Splitpanes, Pane } from 'splitpanes';
import 'splitpanes/dist/splitpanes.css';
import { createFormData, getSSRdata } from '../../../utils/utils';
import axios from 'axios';

const sandbox = document.createElement("iframe");
sandbox.style.display = 'none';
document.body.append(sandbox);

sandbox.contentWindow.self.eval(/* JS */`
class JudgeError extends Error {
    constructor(reason) {
        super(reason);
        this.reason = reason;
    }
}
window.JudgeError = JudgeError;
class EscapeError extends Error {
    constructor() {
        super("");
    }
}
window.EscapeError = EscapeError;
async function runSandbox(task, output = []) {
    const __log__ = console.log;
    console.log = (value) => {
        output.push(value.toString());
    }
    try {
        await task(output);
        return output;
    } catch(e) {
        throw e;
    } finally {
        console.log = __log__;
    }
}
window.runSandbox = runSandbox;
`);
function checkSyntax(script) {
    try {
        sandbox.contentWindow.self.eval(/* JS */`
        () => {
            ${ script }
        }
        `);
        return null;
    } catch (e) {
        return e;
    }
}
async function run(script, input) {
    const res = checkSyntax(script);
    if (res !== null) {
        return { status: 'syntax error', message: res.toString() };
    }
    try {
        const output = await sandbox.contentWindow.self.eval(/* JS */`
        'use strict';
        runSandbox(async () => {
            var input = JSON.parse(decodeURIComponent("${ encodeURIComponent(JSON.stringify(input)) }"));
            ${ script };
        })`);
        return { status: 'normal', message: output.join("\n") };
    } catch(e) {
        return { status: 'runtime error', message: e.toString() };
    }
}

async function judge(script, input, judger) {
    const res = checkSyntax(script);
    if (res !== null) {
        return { status: 'syntax error', message: res.toString() };
    }
    const innerPrefix = "__99BA5433DF5FA898C8E078B8BA55F251__";
    if (script.includes(innerPrefix)) {
        return { status: 'runtime error', message: "请勿攻击评测程序" };
    }
    const createKey = (name) => `${ innerPrefix }${ name }__${ (Math.random() * 100000).toFixed() }`;
    const escapeKey = createKey("escape");
    const outputKey = createKey("output");
    try {
        await sandbox.contentWindow.self.eval(/* JS */`
        'use strict';
        let ${ escapeKey } = true;
        const ${ outputKey } = [];
        runSandbox(async () => {
            var input = JSON.parse(decodeURIComponent("${ encodeURIComponent(JSON.stringify(input)) }"));
            ${ script };
            (() => {
                const input = JSON.parse(decodeURIComponent("${ encodeURIComponent(JSON.stringify(input)) }"));
                const output = ${ outputKey };
                const assert = (condition, reason) => {
                    if (!condition) throw new JudgeError(reason);
                }
                ${ escapeKey } = false;
                ${ judger };
            })();
        }, ${ outputKey }).then(() => {
            if (${ escapeKey }) throw new EscapeError();
        })`);
        return { status: 'accept', message: "" };
    } catch(e) {
        if (e instanceof sandbox.contentWindow.self.JudgeError) {
            return { status: 'wrong answer', message: e.reason };
        } else if (e instanceof sandbox.contentWindow.self.EscapeError) {
            return { status: 'runtime error', message: "检测程序被跳过, 请勿在代码顶层使用return!" };
        } else {
            return { status: 'runtime error', message: e.toString() };
        }
    }
}

export default {
    data: () => ({
        tid: 0,
        title: "",
        content: "",
        initcode: "",
        inputs: [],
        judger: "",
        output: "",
        passed: false,
        status: "unjudge",
    }),
    created() {
        const { tid, title, content: markdown, passed } = getSSRdata("task");
        const { content, initcode, inputs, judger } = parseTaskMarkdown(markdown);
        this.tid = tid;
        this.passed = passed;
        this.title = title;
        this.content = content;
        this.initcode = initcode;
        this.inputs = inputs;
        this.judger = judger;
    },
    mounted() {
        const editor = monaco.editor.create(this.$refs.editor, {
            language: "javascript",
            automaticLayout: true,
        });
        this.editor = editor;
        this.reset();
    },
    methods: {
        reset() {
            this.editor.setValue(this.initcode);
            this.output = "";
        },
        async run() {
            if (this.judgering) {
                return;
            }
            this.judgering = true;
            const script = this.editor.getValue();
            const input = this.inputs[0];
            const res = await run(script, input);
            this.status = res.status;
            this.output = res.message;
            this.judgering = false;
        },
        async judge() {
            if (this.judgering) {
                return;
            }
            this.judgering = true;
            const script = this.editor.getValue();
            const res = await (async () => {
                for (let i = 0; i < this.inputs.length; i++) {
                    const input = this.inputs[i];
                    const res = await judge(script, input, this.judger);
                    if (res.status !== 'accpet') return res;
                }
                return { status: 'accept', message: '' };
            })();
            this.status = res.status;
            this.output = res.message;
            if (this.status === 'accept') {
                this.passed = true;
            }
            axios.post("/backend/codelab/submit.php", createFormData({
                tid: this.tid, status: res.status, script
            })),
            this.judgering = false;
        },
    },
    components: {
        Header, Footer, Splitpanes, Pane,
    }
}
</script>

<style lang="less">
html, body {
    height: 100%;
    width: 100%;
    margin: 0;
}
#app {
    font-family: Avenir, Helvetica, Arial, sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-align: center;
    color: #2c3e50;
    min-height: 100%;
    display: flex;
    flex-direction: column;
    cursor: default;
}
</style>

<style lang="less" scoped>
.adjuster {
    margin: 0px auto;
    flex-grow: 1;
    position: relative;
    width: 100%;
}
.brench {
    height: 100%;
    position: absolute;
}
.task-text {
    text-align: left;
    padding: 5px;
}
.title {
    font-size: 20px;
    margin-bottom: 2px;
}
.editor {
    height: 100%;
    text-align: left;
}
.splitpanes--vertical::v-deep .splitpanes__pane {
    transition: none;
    -webkit-transition: none;
}
.splitpanes--horizontal::v-deep .splitpanes__pane {
    transition: none;
    -webkit-transition: none;
}
.splitpanes::v-deep .splitpanes__splitter {
    background: rgba(0, 0, 0, 0.15);
    min-width: 2px;
}
.toolbar {
    display: flex;
    align-items: center;
    padding: 2px;
    box-shadow: 0px 2px 2px rgba(0, 0, 0, 0.35);
    .status {
        font-size: 18px;
        font-weight: bold;
        margin-right: auto;
    }
    .ant-btn {
        margin: 0 5px;
    }
}
.output {
    text-align: left;
}
.accept {
    color: #52c41a;
}
.normal {
    color: #1890ff;
}
.syntax-error, .wrong-answer, .runtime-error {
    color: #f5222d;
}
</style>